// app/frontend/pages/Admin/Categories/Form.tsx
import { useState } from "react";
import {
  Box,
  Stack,
  Group,
  Paper,
  NumberInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconArrowLeft, IconDeviceFloppy } from "@tabler/icons-react";
import { Link, router } from "@inertiajs/react";
import { L2KH2, L2KText, L2KCaption } from "../../../components/ui/Typography";
import { L2KInput, L2KTextarea, L2KButton } from "../../../components/ui/Forms";
import { l2kNotify } from "../../../components/ui/Feedback";
import AdminLayout from "../../../components/layouts/AdminLayout";
import { SEO } from "../../../components/common/SEO";

interface Category {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  icon: string | null;
  position: number;
}

interface Props {
  category: Category | null;
  is_new: boolean;
  errors?: string[];
}

export default function CategoriesForm({ category, is_new }: Props) {
  const [saving, setSaving] = useState(false);

  const form = useForm({
    initialValues: {
      name: category?.name || "",
      slug: category?.slug || "",
      description: category?.description || "",
      icon: category?.icon || "",
      position: category?.position || 0,
    },
    validate: {
      name: (value) => (value.length < 2 ? "Name must be at least 2 characters" : null),
    },
  });

  // Auto-generate slug from name
  const handleNameChange = (value: string) => {
    form.setFieldValue("name", value);
    if (is_new || !category?.slug) {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
      form.setFieldValue("slug", slug);
    }
  };

  const handleSubmit = async (values: typeof form.values) => {
    setSaving(true);
    
    const url = is_new 
      ? "/admin/categories" 
      : `/admin/categories/${category?.id}`;
    
    const method = is_new ? "POST" : "PATCH";

    try {
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "",
          Accept: "application/json",
        },
        body: JSON.stringify({ category: values }),
      });

      const data = await response.json();

      if (data.success) {
        l2kNotify.success(
          is_new ? "Created" : "Saved",
          `Category ${is_new ? "created" : "updated"} successfully`
        );
        router.visit("/admin/categories");
      } else {
        l2kNotify.error("Error", data.errors?.join(", ") || "Failed to save category");
      }
    } catch (error) {
      l2kNotify.error("Error", "Failed to save category");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SEO title={`${is_new ? "New" : "Edit"} Category - Admin - Learntokiz`} />
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <Group>
            <Link href="/admin/categories">
              <L2KButton variant="ghost" leftSection={<IconArrowLeft size={16} />}>
                Back
              </L2KButton>
            </Link>
            <Box>
              <L2KH2 mb={0}>{is_new ? "New Category" : "Edit Category"}</L2KH2>
            </Box>
          </Group>
          <L2KButton
            leftSection={<IconDeviceFloppy size={16} />}
            loading={saving}
            onClick={() => form.onSubmit(handleSubmit)()}
          >
            {is_new ? "Create Category" : "Save Changes"}
          </L2KButton>
        </Group>

        {/* Form */}
        <Paper
          p="lg"
          radius="md"
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.03)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            maxWidth: 600,
          }}
        >
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="md">
              <L2KInput
                label="Name"
                placeholder="Category name"
                required
                value={form.values.name}
                onChange={(e) => handleNameChange(e.target.value)}
                error={form.errors.name}
              />

              <L2KInput
                label="Slug"
                placeholder="category-slug"
                description="URL-friendly identifier (auto-generated from name)"
                {...form.getInputProps("slug")}
              />

              <L2KTextarea
                label="Description"
                placeholder="Optional description for this category"
                minRows={3}
                {...form.getInputProps("description")}
              />

              <L2KInput
                label="Icon"
                placeholder="Icon name (e.g., moves, drills)"
                description="Used for display in the UI"
                {...form.getInputProps("icon")}
              />

              <NumberInput
                label="Position"
                placeholder="Display order"
                description="Lower numbers appear first"
                min={0}
                {...form.getInputProps("position")}
                styles={{
                  label: {
                    color: "#F0F0F0",
                    fontFamily: "'Public Sans', sans-serif",
                    fontWeight: 500,
                    marginBottom: 6,
                  },
                  input: {
                    backgroundColor: "rgba(255, 255, 255, 0.03)",
                    borderColor: "rgba(255, 152, 0, 0.2)",
                    color: "#F0F0F0",
                    "&:focus": {
                      borderColor: "#FF9800",
                    },
                  },
                  description: {
                    color: "#B8B8B8",
                    fontFamily: "'Public Sans', sans-serif",
                  },
                }}
              />
            </Stack>
          </form>
        </Paper>

        {/* Info */}
        {!is_new && category && (
          <Paper
            p="lg"
            radius="md"
            style={{
              backgroundColor: "rgba(255, 255, 255, 0.03)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              maxWidth: 600,
            }}
          >
            <Stack gap="xs">
              <L2KCaption variant="tertiary">Category ID: {category.id}</L2KCaption>
            </Stack>
          </Paper>
        )}
      </Stack>
    </>
  );
}

CategoriesForm.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>;
